/*
Date : 2026-05-04
- Modifications précédentes : Création initiale du composant InstagramFeed avec carousel horizontal, ordre aléatoire à chaque chargement, intégration des 13 posts Instagram fournis, badges Post/Vidéo et CTA vers le compte Yogi Market.
- Nouvelle modification : Suppression des badges Post/Vidéo, suppression du cadre visuel derrière les embeds Instagram, transformation en vrai carrousel contrôlé par flèches sans barre de scroll visible, homogénéisation de la taille des publications, remplacement de la phrase finale par un appel à suivre Instagram pour les promotions, nouveautés et concerts à venir.
*/

import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ExternalLink, Instagram } from "lucide-react";

const instagramPosts = [
  {
    id: "DV6MVXGjLcX",
    url: "https://www.instagram.com/p/DV6MVXGjLcX/",
  },
  {
    id: "DVs1l8iDLwI",
    url: "https://www.instagram.com/p/DVs1l8iDLwI/",
  },
  {
    id: "DT2HCHDCDKq",
    url: "https://www.instagram.com/p/DT2HCHDCDKq/",
  },
  {
    id: "DTMjVFijBjm",
    url: "https://www.instagram.com/p/DTMjVFijBjm/",
  },
  {
    id: "DTKw22DjI97",
    url: "https://www.instagram.com/p/DTKw22DjI97/",
  },
  {
    id: "DSd7lRwjAxt",
    url: "https://www.instagram.com/p/DSd7lRwjAxt/",
  },
  {
    id: "DSDaFx3CL18",
    url: "https://www.instagram.com/p/DSDaFx3CL18/",
  },
  {
    id: "DRB4zu3CI03",
    url: "https://www.instagram.com/p/DRB4zu3CI03/",
  },
  {
    id: "DPtsMeajCpb",
    url: "https://www.instagram.com/p/DPtsMeajCpb/",
  },
  {
    id: "DPoBg8YCG_F",
    url: "https://www.instagram.com/p/DPoBg8YCG_F/",
  },
  {
    id: "DPZp6CvjGhi",
    url: "https://www.instagram.com/p/DPZp6CvjGhi/",
  },
  {
    id: "DPVnvcAiBIu",
    url: "https://www.instagram.com/p/DPVnvcAiBIu/",
  },
  {
    id: "Cx6H6T8s42q",
    url: "https://www.instagram.com/p/Cx6H6T8s42q/",
  },
];

function shuffleArray(items) {
  const shuffled = [...items];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [
      shuffled[randomIndex],
      shuffled[index],
    ];
  }

  return shuffled;
}

function getVisiblePosts(posts, startIndex, count) {
  return Array.from({ length: count }, (_, offset) => {
    return posts[(startIndex + offset) % posts.length];
  });
}

export default function InstagramFeed() {
  const randomizedPosts = useMemo(() => shuffleArray(instagramPosts), []);
  const [activeIndex, setActiveIndex] = useState(0);

  const visiblePosts = getVisiblePosts(randomizedPosts, activeIndex, 4);

  useEffect(() => {
    const processEmbeds = () => {
      if (window.instgrm?.Embeds?.process) {
        window.instgrm.Embeds.process();
      }
    };

    const existingScript = document.querySelector(
      'script[src="https://www.instagram.com/embed.js"]'
    );

    if (existingScript) {
      processEmbeds();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://www.instagram.com/embed.js";
    script.async = true;
    script.onload = processEmbeds;
    document.body.appendChild(script);
  }, [visiblePosts]);

  const goToPrevious = () => {
    setActiveIndex((currentIndex) =>
      currentIndex === 0 ? randomizedPosts.length - 1 : currentIndex - 1
    );
  };

  const goToNext = () => {
    setActiveIndex((currentIndex) => (currentIndex + 1) % randomizedPosts.length);
  };

  return (
    <section
      id="instagram"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-cream overflow-hidden"
      data-testid="instagram-section"
    >
      <div className="absolute -top-24 -right-20 w-[420px] h-[420px] rounded-full bg-terracotta/10 blur-[110px] pointer-events-none" />
      <div className="absolute -bottom-24 -left-20 w-[420px] h-[420px] rounded-full bg-sage/15 blur-[110px] pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-terracotta/10 border border-terracotta/20 mb-7">
              <Instagram size={15} className="text-terracotta" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-terracotta font-medium">
                Instagram
              </span>
            </div>

            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[0.95] text-ink">
              Dans les coulisses de{" "}
              <span className="italic-serif text-terracotta">Yogi Market.</span>
            </h2>

            <p className="mt-6 text-base md:text-lg text-ink/70 font-light leading-relaxed max-w-2xl">
              Suivez nos arrivages, promotions, nouveautés, concerts à venir et
              moments du studio directement sur Instagram.
            </p>
          </motion.div>

          <motion.a
            href="https://www.instagram.com/yogimarket.fr/"
            target="_blank"
            rel="noreferrer"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="group inline-flex w-fit items-center gap-3 px-6 py-3 rounded-full bg-ink text-cream text-xs uppercase tracking-[0.15em] hover:bg-terracotta transition-all duration-500"
            data-testid="instagram-follow"
          >
            Suivre @yogimarket.fr
            <ExternalLink
              size={14}
              className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
            />
          </motion.a>
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={goToPrevious}
            className="absolute z-20 left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 hidden md:flex w-12 h-12 rounded-full bg-ink text-cream shadow-[0_20px_60px_-25px_rgba(0,0,0,0.5)] hover:bg-terracotta transition items-center justify-center"
            aria-label="Voir les publications Instagram précédentes"
            data-testid="instagram-prev"
          >
            <ArrowLeft size={18} />
          </button>

          <button
            type="button"
            onClick={goToNext}
            className="absolute z-20 right-0 top-1/2 -translate-y-1/2 translate-x-1/2 hidden md:flex w-12 h-12 rounded-full bg-ink text-cream shadow-[0_20px_60px_-25px_rgba(0,0,0,0.5)] hover:bg-terracotta transition items-center justify-center"
            aria-label="Voir les publications Instagram suivantes"
            data-testid="instagram-next"
          >
            <ArrowRight size={18} />
          </button>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6"
            data-testid="instagram-carousel"
          >
            {visiblePosts.map((post, index) => (
              <motion.article
                key={`${post.id}-${activeIndex}`}
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.55,
                  delay: index * 0.06,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="relative overflow-hidden rounded-[26px] bg-transparent"
                data-testid={`instagram-post-${post.id}`}
              >
                <div className="h-[560px] md:h-[600px] overflow-hidden rounded-[26px] bg-cream">
                  <blockquote
                    className="instagram-media"
                    data-instgrm-permalink={`${post.url}?utm_source=ig_embed&utm_campaign=loading`}
                    data-instgrm-version="14"
                    style={{
                      background: "#FFF",
                      border: "0",
                      borderRadius: "0",
                      boxShadow: "none",
                      margin: "0",
                      minWidth: "100%",
                      maxWidth: "100%",
                      width: "100%",
                    }}
                  >
                    <a href={post.url} target="_blank" rel="noreferrer">
                      Voir cette publication sur Instagram
                    </a>
                  </blockquote>
                </div>
              </motion.article>
            ))}
          </div>

          <div className="mt-8 flex md:hidden items-center justify-center gap-3">
            <button
              type="button"
              onClick={goToPrevious}
              className="w-11 h-11 rounded-full bg-ink text-cream hover:bg-terracotta transition flex items-center justify-center"
              aria-label="Voir les publications Instagram précédentes"
            >
              <ArrowLeft size={17} />
            </button>

            <button
              type="button"
              onClick={goToNext}
              className="w-11 h-11 rounded-full bg-ink text-cream hover:bg-terracotta transition flex items-center justify-center"
              aria-label="Voir les publications Instagram suivantes"
            >
              <ArrowRight size={17} />
            </button>
          </div>
        </div>

        <p className="mt-8 text-center text-sm md:text-base text-ink/60 font-light">
          Retrouvez les nouveautés, les offres ponctuelles et les prochains
          événements directement sur Instagram.
        </p>
      </div>
    </section>
  );
}